import React from 'react';
import { Card, BlockStack, InlineStack, Text, ProgressBar, Badge, Divider } from '@shopify/polaris';
import { AnimatedCounter } from './ui';

/**
 * T074: IdentifierCoverageCard component.
 * Coverage %, valid/invalid/missing counts, SKU/MPN counts, progress bar.
 */

interface IdentifierCoverageCardProps {
  totalProducts: number;
  validGtins: number;
  invalidGtins: number;
  missingGtins: number;
  withSku: number;
  withMpn: number;
  coveragePercent: number;
}

export function IdentifierCoverageCard({
  totalProducts,
  validGtins,
  invalidGtins,
  missingGtins,
  withSku,
  withMpn,
  coveragePercent,
}: IdentifierCoverageCardProps): React.ReactElement {
  const coverageTone =
    coveragePercent >= 80
      ? 'success'
      : coveragePercent >= 50
        ? 'warning'
        : 'critical';

  return (
    <Card>
      <BlockStack gap="400">
        <Text as="h2" variant="headingMd">
          Identifier Coverage
        </Text>

        <BlockStack gap="200">
          <InlineStack align="space-between">
            <Text as="span" variant="bodyMd" fontWeight="semibold">
              Overall Coverage
            </Text>
            <AnimatedCounter value={coveragePercent} suffix="%" variant="bodyMd" fontWeight="semibold" />
          </InlineStack>
          <ProgressBar
            progress={coveragePercent}
            tone={coverageTone === 'warning' ? 'highlight' : coverageTone}
            size="small"
          />
        </BlockStack>

        <Divider />

        <Text as="h3" variant="headingSm">
          GTIN / Barcode
        </Text>
        <BlockStack gap="200">
          <InlineStack align="space-between">
            <Text as="span" variant="bodySm">Valid GTINs</Text>
            <Badge tone="success">{String(validGtins)}</Badge>
          </InlineStack>
          <InlineStack align="space-between">
            <Text as="span" variant="bodySm">Invalid GTINs</Text>
            <Badge tone="critical">{String(invalidGtins)}</Badge>
          </InlineStack>
          <InlineStack align="space-between">
            <Text as="span" variant="bodySm">Missing GTINs</Text>
            <Badge tone="warning">{String(missingGtins)}</Badge>
          </InlineStack>
        </BlockStack>

        <Divider />

        <Text as="h3" variant="headingSm">
          Other Identifiers
        </Text>
        <BlockStack gap="200">
          <InlineStack align="space-between">
            <Text as="span" variant="bodySm">With SKU</Text>
            <Text as="span" variant="bodySm">{withSku} / {totalProducts}</Text>
          </InlineStack>
          <InlineStack align="space-between">
            <Text as="span" variant="bodySm">With MPN</Text>
            <Text as="span" variant="bodySm">{withMpn} / {totalProducts}</Text>
          </InlineStack>
        </BlockStack>
      </BlockStack>
    </Card>
  );
}
